import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  DatabaseBackup,
  Download,
  FileJson,
  RefreshCw,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import { usePatients } from "@/lib/usePatients";
import { toast } from "@/lib/safeToast";
import { formatBytes } from "@/lib/backup/v1/canonical";
import { downloadBackupFile, exportBackup } from "@/lib/backup/v1/exportBackup";
import { downloadPiruFile, exportPiruBackup } from "@/lib/backup/v1/piruExport";
import { inspectBackup, prepareBackupForRestore, type BackupInspection } from "@/lib/backup/v1/inspectBackup";
import { previewRestore, restoreBackup } from "@/lib/backup/v1/restoreBackup";
import type { BackupScope, RestoreMode, RestorePlan, RestoreProgress } from "@/lib/backup/v1/types";

const MAX_IMPORT_BYTES = 25 * 1024 * 1024;

function ProgressLine({ progress }: { progress: RestoreProgress | null }) {
  if (!progress) return null;
  const value = progress.total && progress.current ? Math.round((progress.current / progress.total) * 100) : undefined;
  return (
    <div className="space-y-1.5 rounded-2xl border border-border/60 bg-muted/20 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
        <span>{progress.phase}</span>
        {progress.current && progress.total ? <span className="ml-auto font-mono">{progress.current}/{progress.total}</span> : null}
      </div>
      {typeof value === "number" ? <Progress value={value} className="h-1.5" /> : null}
    </div>
  );
}

function ToggleRow({ checked, onCheckedChange, title, description }: {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 p-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function InspectionCard({ inspection, fileName, fileSize }: { inspection: BackupInspection; fileName: string; fileSize: number }) {
  const checksum = inspection.checksumStatus;
  return (
    <Card className="rounded-3xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
          <FileJson className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{fileName}</div>
          <div className="text-xs text-muted-foreground">{inspection.label} · {formatBytes(fileSize)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {inspection.appVersion ?? "versão não informada"} · {inspection.exportedAt ? new Date(inspection.exportedAt).toLocaleString("pt-BR") : "data não informada"}
          </div>
        </div>
        <Badge variant={inspection.errors.length ? "destructive" : "outline"} className="shrink-0">
          {inspection.errors.length ? "bloqueado" : "válido"}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px]">
        <Badge variant="secondary">{inspection.totalRows} itens</Badge>
        {checksum === "valid" ? <Badge variant="outline" className="gap-1"><ShieldCheck className="h-3 w-3" />SHA-256 válido</Badge> : null}
        {checksum === "missing" ? <Badge variant="outline">sem checksum</Badge> : null}
        {checksum === "invalid" ? <Badge variant="destructive">checksum inválido</Badge> : null}
      </div>

      {inspection.errors.length ? (
        <div className="space-y-1 rounded-2xl border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
          {inspection.errors.map((error) => <div key={error}>• {error}</div>)}
        </div>
      ) : null}
      {inspection.warnings.length ? (
        <div className="space-y-1 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-3 text-xs text-muted-foreground">
          {inspection.warnings.map((warning) => <div key={warning}>• {warning}</div>)}
        </div>
      ) : null}
    </Card>
  );
}

function RestorePlanCard({ plan }: { plan: RestorePlan }) {
  return (
    <Card className="rounded-3xl p-4 space-y-3">
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className="rounded-2xl bg-muted/50 p-2"><div className="text-lg font-semibold">{plan.toCreate}</div><div className="text-[10px] text-muted-foreground">criar</div></div>
        <div className="rounded-2xl bg-muted/50 p-2"><div className="text-lg font-semibold">{plan.toUpdate}</div><div className="text-[10px] text-muted-foreground">atualizar</div></div>
        <div className="rounded-2xl bg-muted/50 p-2"><div className="text-lg font-semibold">{plan.toPreserve}</div><div className="text-[10px] text-muted-foreground">preservar</div></div>
        <div className="rounded-2xl bg-muted/50 p-2"><div className="text-lg font-semibold">{plan.skipped}</div><div className="text-[10px] text-muted-foreground">ignorar</div></div>
      </div>
      <div className="space-y-2">
        {plan.entities.filter((entity) => entity.total > 0).map((entity) => (
          <div key={entity.table} className="flex items-center gap-2 rounded-xl border border-border/50 px-3 py-2 text-xs">
            <div className="min-w-0 flex-1 truncate">{entity.label}</div>
            {entity.toCreate ? <span className="font-mono text-emerald-600">+{entity.toCreate}</span> : null}
            {entity.toUpdate ? <span className="font-mono text-blue-600">~{entity.toUpdate}</span> : null}
            {entity.toPreserve ? <span className="font-mono text-muted-foreground">={entity.toPreserve}</span> : null}
            {entity.skipped ? <span className="font-mono text-amber-600">!{entity.skipped}</span> : null}
          </div>
        ))}
      </div>
      {plan.warnings.length ? (
        <div className="space-y-1 text-xs text-muted-foreground">
          {plan.warnings.map((warning) => <div key={warning}>• {warning}</div>)}
        </div>
      ) : null}
    </Card>
  );
}

export default function BackupPage() {
  const { user } = useAuth();
  const patients = usePatients();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [scopeKind, setScopeKind] = useState<"full" | "patient">("full");
  const [exportPatientId, setExportPatientId] = useState("");
  const [includePharmacology, setIncludePharmacology] = useState(true);
  const [includeDerived, setIncludeDerived] = useState(true);
  const [includeAuditHistory, setIncludeAuditHistory] = useState(false);
  const [busyExport, setBusyExport] = useState(false);

  const [importFile, setImportFile] = useState<File | null>(null);
  const [inspection, setInspection] = useState<BackupInspection | null>(null);
  const [importPatientId, setImportPatientId] = useState("");
  const [mode, setMode] = useState<RestoreMode>("merge");
  const [plan, setPlan] = useState<RestorePlan | null>(null);
  const [prepared, setPrepared] = useState<ReturnType<typeof prepareBackupForRestore> | null>(null);
  const [progress, setProgress] = useState<RestoreProgress | null>(null);
  const [busyImport, setBusyImport] = useState(false);

  const patientOptions = patients.data ?? [];
  const canExport = !!user && (scopeKind === "full" || !!exportPatientId);
  const canAnalyze = !!user && !!inspection && inspection.errors.length === 0 && (!inspection.requiresPatient || !!importPatientId);

  const totalDatabaseRows = useMemo(() => plan?.total ?? inspection?.totalRows ?? 0, [plan, inspection]);

  async function handleExport() {
    if (!user || !canExport) return;
    setBusyExport(true);
    setProgress({ phase: "Preparando backup" });
    try {
      const scope: BackupScope = {
        kind: scopeKind,
        ...(scopeKind === "patient" ? { patientId: exportPatientId } : {}),
        includePharmacology,
        includeDerived,
        includeAuditHistory,
      };
      const backup = await exportBackup(user.id, scope, setProgress);
      const result = downloadBackupFile(backup);
      localStorage.setItem("psn:last_export", backup.exportedAt);
      toast.success(`Backup gerado: ${result.name} (${formatBytes(result.size)}).`);
    } catch (error: any) {
      toast.error(error?.message ?? "Falha ao gerar backup.");
    } finally {
      setBusyExport(false);
      setProgress(null);
    }
  }

  async function handlePiruExport() {
    if (!user || scopeKind !== "patient" || !exportPatientId) {
      toast.error("Para exportar ao Piru, selecione “Um paciente” e escolha o destino.");
      return;
    }
    setBusyExport(true);
    setProgress({ phase: "Preparando exportação compatível com Piru" });
    try {
      const { file } = await exportPiruBackup(user.id, exportPatientId, setProgress);
      const result = downloadPiruFile(file);
      toast.success("JSON compatível com Piru gerado: " + result.name + " (" + formatBytes(result.size) + ").");
    } catch (error: any) {
      toast.error(error?.message ?? "Falha ao exportar para Piru.");
    } finally {
      setBusyExport(false);
      setProgress(null);
    }
  }

  async function handleFile(file: File | null) {
    setImportFile(file);
    setInspection(null);
    setPlan(null);
    setPrepared(null);
    if (!file) return;
    if (file.size > MAX_IMPORT_BYTES) {
      toast.error(`Arquivo acima do limite de ${formatBytes(MAX_IMPORT_BYTES)}.`);
      return;
    }
    setBusyImport(true);
    setProgress({ phase: "Lendo e verificando arquivo" });
    try {
      const text = await file.text();
      const raw = JSON.parse(text);
      const result = await inspectBackup(raw);
      setInspection(result);
      if (result.requiresPatient && !importPatientId && patientOptions[0]?.id) setImportPatientId(patientOptions[0].id);
      if (result.errors.length) toast.error("O arquivo foi lido, mas contém erro que impede restauração.");
      else toast.success(`${result.label} reconhecido.`);
    } catch (error: any) {
      toast.error(error instanceof SyntaxError ? "JSON inválido." : (error?.message ?? "Falha ao ler arquivo."));
    } finally {
      setBusyImport(false);
      setProgress(null);
    }
  }

  async function analyzeRestore() {
    if (!user || !inspection || !canAnalyze) return;
    setBusyImport(true);
    setProgress({ phase: "Montando prévia da restauração" });
    try {
      const backup = prepareBackupForRestore(inspection, user.id, importPatientId || undefined);
      const nextPlan = await previewRestore(backup, user.id, mode, setProgress);
      setPrepared(backup);
      setPlan(nextPlan);
      toast.success("Prévia concluída. Nenhum dado foi alterado.");
    } catch (error: any) {
      toast.error(error?.message ?? "Falha ao analisar restauração.");
    } finally {
      setBusyImport(false);
      setProgress(null);
    }
  }

  async function executeRestore() {
    if (!user || !prepared || !plan) return;
    const message = mode === "merge"
      ? `Restaurar ${plan.toCreate} novo(s) registro(s) e preservar ${plan.toPreserve} existente(s)? Nenhum dado será apagado.`
      : `Restaurar ${plan.toCreate} novo(s) e atualizar ${plan.toUpdate} existente(s)? Nenhum dado será apagado.`;
    if (!confirm(message)) return;

    setBusyImport(true);
    setProgress({ phase: "Iniciando restauração" });
    try {
      const outcome = await restoreBackup(prepared, user.id, mode, setProgress);
      await queryClient.invalidateQueries();
      const created = Object.values(outcome.created).reduce((a, b) => a + b, 0);
      const updated = Object.values(outcome.updated).reduce((a, b) => a + b, 0);
      if (outcome.errors.length) {
        toast.error(`Restauração parcial: ${created} criado(s), ${updated} atualizado(s), ${outcome.errors.length} erro(s).`);
      } else {
        toast.success(`Restauração concluída: ${created} criado(s), ${updated} atualizado(s).`);
      }
      const nextPlan = await previewRestore(prepared, user.id, mode);
      setPlan(nextPlan);
    } catch (error: any) {
      toast.error(error?.message ?? "Falha ao restaurar backup.");
    } finally {
      setBusyImport(false);
      setProgress(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 pb-8">
      <header className="space-y-1 px-1 pt-1">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Dados</div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Backup e restauração</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Backup versionado, verificável e restaurável. Importações são analisadas antes de escrever no banco e nunca apagam registros automaticamente.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        <Card className="rounded-3xl p-4 sm:p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary"><DatabaseBackup className="h-5 w-5" /></div>
            <div><h2 className="font-semibold">Exportar</h2><p className="text-xs text-muted-foreground">ClinicalOS Backup v1 + SHA-256.</p></div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant={scopeKind === "full" ? "default" : "outline"} onClick={() => setScopeKind("full")}>Conta inteira</Button>
            <Button variant={scopeKind === "patient" ? "default" : "outline"} onClick={() => setScopeKind("patient")}>Um paciente</Button>
          </div>

          {scopeKind === "patient" ? (
            <select className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm" value={exportPatientId} onChange={(event) => setExportPatientId(event.target.value)}>
              <option value="">Selecione o paciente</option>
              {patientOptions.map((patient) => <option key={patient.id} value={patient.id}>{patient.social_name || patient.full_name}</option>)}
            </select>
          ) : null}

          <ToggleRow checked={includePharmacology} onCheckedChange={setIncludePharmacology} title="Farmacologia privada" description="Cópias privadas, formulações e parâmetros editáveis. Canônicos globais entram apenas como referência." />
          <ToggleRow checked={includeDerived} onCheckedChange={setIncludeDerived} title="Resultados derivados" description="Relatórios, análises, forecasts e resultados que podem ser recalculados." />
          <ToggleRow checked={includeAuditHistory} onCheckedChange={setIncludeAuditHistory} title="Histórico de auditoria" description="Inclui logs históricos no arquivo, mas eles não são reaplicados durante a restauração." />

          <div className="rounded-2xl border border-border/60 bg-muted/20 p-3 text-xs leading-relaxed text-muted-foreground">
            Tokens, senhas, chaves, sessões de autenticação e referências secretas de integrações são excluídos do arquivo.
          </div>

          <Button className="w-full" disabled={!canExport || busyExport} onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />Gerar backup ClinicalOS
          </Button>
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground">
            <div className="font-medium text-foreground">Compatibilidade com Piru</div>
            Exporta doses, notas/check-ins, sinais vitais como notas, tratamentos ativos como rotina e substâncias privadas referenciadas para um JSON Piru v1. O estoque é enviado como fotografia do saldo atual para evitar descontar novamente o consumo histórico. A exportação é individual por paciente e não substitui o backup ClinicalOS completo.
          </div>
          <Button className="w-full" variant="outline" disabled={!user || scopeKind !== "patient" || !exportPatientId || busyExport} onClick={handlePiruExport}>
            <FileJson className="mr-2 h-4 w-4" />Exportar JSON compatível com Piru
          </Button>
          {scopeKind !== "patient" || !exportPatientId ? <div className="text-[11px] text-muted-foreground">Selecione “Um paciente” e escolha o paciente para habilitar a exportação Piru. O arquivo não inclui o ID/nome cadastrado do paciente, mas textos livres podem conter dados identificáveis; trate o JSON como dado clínico sensível.</div> : null}
        </Card>

        <Card className="rounded-3xl p-4 sm:p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary"><Upload className="h-5 w-5" /></div>
            <div><h2 className="font-semibold">Importar / restaurar</h2><p className="text-xs text-muted-foreground">ClinicalOS v1, ClinicalOS antigo e Piru v1.</p></div>
          </div>

          <Button variant="outline" className="w-full" disabled={busyImport} onClick={() => fileRef.current?.click()}>
            <FileJson className="mr-2 h-4 w-4" />Escolher JSON
          </Button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={(event) => handleFile(event.target.files?.[0] ?? null)} />

          {inspection?.requiresPatient ? (
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Paciente de destino</label>
              <select className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm" value={importPatientId} onChange={(event) => { setImportPatientId(event.target.value); setPlan(null); setPrepared(null); }}>
                <option value="">Selecione</option>
                {patientOptions.map((patient) => <option key={patient.id} value={patient.id}>{patient.social_name || patient.full_name}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground">Piru não carrega identidade clínica; o destino precisa ser escolhido explicitamente.</p>
            </div>
          ) : null}

          {inspection && !inspection.errors.length ? (
            <div className="space-y-2">
              <div className="text-xs font-medium">Política de conflito</div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant={mode === "merge" ? "default" : "outline"} onClick={() => { setMode("merge"); setPlan(null); setPrepared(null); }}>Mesclar</Button>
                <Button variant={mode === "update" ? "default" : "outline"} onClick={() => { setMode("update"); setPlan(null); setPrepared(null); }}>Atualizar</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Mesclar preserva registros já existentes. Atualizar reaplica dados do backup quando o ID/chave natural já existe. Nenhum modo apaga dados ausentes no arquivo.
              </p>
            </div>
          ) : null}

          {inspection ? <div className="text-xs text-muted-foreground">Conteúdo detectado: <b className="text-foreground">{totalDatabaseRows}</b> itens.</div> : null}

          {inspection && !inspection.errors.length ? (
            <Button className="w-full" variant="secondary" disabled={!canAnalyze || busyImport} onClick={analyzeRestore}>
              <ShieldCheck className="mr-2 h-4 w-4" />Analisar sem alterar dados
            </Button>
          ) : null}
        </Card>
      </div>

      <ProgressLine progress={progress} />

      {inspection && importFile ? <InspectionCard inspection={inspection} fileName={importFile.name} fileSize={importFile.size} /> : null}

      {plan ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <h2 className="font-semibold">Prévia de restauração</h2>
          </div>
          <RestorePlanCard plan={plan} />
          <Button className="w-full sm:w-auto" disabled={busyImport || (plan.toCreate + plan.toUpdate === 0)} onClick={executeRestore}>
            <Upload className="mr-2 h-4 w-4" />Restaurar agora
          </Button>
        </div>
      ) : null}

      <Card className="rounded-3xl border-dashed p-4 text-xs leading-relaxed text-muted-foreground">
        <div className="mb-1 flex items-center gap-2 font-medium text-foreground"><AlertTriangle className="h-4 w-4" />Regras de segurança</div>
        A prévia não escreve no banco. A restauração é idempotente por ID/chave natural, remapeia owner_id para a conta autenticada, reescreve FKs quando necessário e não sobrescreve canônicos globais. Um registro inválido é isolado para não derrubar o restante do lote.
      </Card>
    </div>
  );
}
